/**
 * Cart Price Calculator
 * Helper functions to calculate cart item prices including options and additionals
 */

import type { CartItem } from './cartStore';
import type { ProductOptionGroup, ProductAdditional } from './productService';

export type CartProductPricing = {
  optionGroups?: ProductOptionGroup[];
  additionals?: ProductAdditional[];
  price?: number;
  promotionPrice?: number | null;
  basePrice?: number;
};

export type CartLineUnitPrices = {
  effective: number;
  original: number;
  hasPromotion: boolean;
  discountPercent: number | null;
};

/** Price charged for one unit of an additional: promo price when set (>0), else base price. */
export function additionalEffectivePrice(additional: ProductAdditional): number {
  const promo = additional.promotionPrice;
  if (promo != null && Number(promo) > 0) {
    return Number(promo);
  }
  return Number(additional.price) || 0;
}

function foodItemTotalWithBase(
  item: CartItem,
  productData: CartProductPricing,
  basePrice: number,
): number {
  const optionGroups = productData.optionGroups || [];
  const additionals = (productData.additionals || []).filter(
    (additional) => additional.active !== false,
  );
  let total = basePrice;

  if (item.foodSelections?.optionGroups) {
    item.foodSelections.optionGroups.forEach((groupSelection) => {
      const group = optionGroups.find((g) => g.id === groupSelection.groupId);
      if (group) {
        groupSelection.optionIds.forEach((optionId) => {
          const option = group.options.find((o) => o.id === optionId);
          if (option) {
            total += Number(option.priceModifier) || 0;
          }
        });
      }
    });
  }

  if (item.foodSelections?.additionals) {
    item.foodSelections.additionals.forEach((additionalSelection) => {
      const additional = additionals.find(
        (a) => a.id === additionalSelection.additionalId,
      );
      if (additional && additionalSelection.quantity > 0) {
        const additionalPrice = additionalEffectivePrice(additional);
        const quantity = Number(additionalSelection.quantity) || 0;
        total += additionalPrice * quantity;
      }
    });
  }

  return total;
}

export function getCartLineUnitPrices(
  item: CartItem,
  productData?: CartProductPricing,
): CartLineUnitPrices {
  const fallbackEffective = Number(item.price) || 0;

  if (!productData) {
    return {
      effective: fallbackEffective,
      original: fallbackEffective,
      hasPromotion: false,
      discountPercent: null,
    };
  }

  if (item.foodSelections) {
    const listBase = Number(productData.price ?? item.price) || 0;
    const promoBase =
      productData.promotionPrice && productData.promotionPrice > 0
        ? productData.promotionPrice
        : listBase;
    const effective = foodItemTotalWithBase(item, productData, promoBase);
    const original = foodItemTotalWithBase(item, productData, listBase);
    const hasPromotion = original > effective && original > 0;

    return {
      effective,
      original,
      hasPromotion,
      discountPercent: hasPromotion
        ? Math.round(((original - effective) / original) * 100)
        : null,
    };
  }

  const effective = fallbackEffective;
  const listPrice = Number(productData.price) ?? effective;
  const promoPrice = productData.promotionPrice;
  const hasPromotion =
    !!promoPrice &&
    promoPrice > 0 &&
    listPrice > 0 &&
    effective < listPrice;

  return {
    effective,
    original: listPrice,
    hasPromotion,
    discountPercent: hasPromotion
      ? Math.round(((listPrice - effective) / listPrice) * 100)
      : null,
  };
}

/**
 * Calculate the total price for a cart item including options and additionals
 * This function fetches product data to get option/additional prices
 */
export async function calculateCartItemPrice(
  item: CartItem,
  productData?: CartProductPricing
): Promise<number> {
  // If no food selections, return the stored price
  if (!item.foodSelections) {
    return item.price;
  }

  // If product data is provided, use it; otherwise fetch it
  let optionGroups: ProductOptionGroup[] = [];
  let additionals: ProductAdditional[] = [];
  let basePrice = item.price;

  if (productData) {
    optionGroups = productData.optionGroups || [];
    additionals = (productData.additionals || []).filter((additional) => additional.active !== false);
    basePrice = productData.basePrice ?? item.price;
  } else {
    // Fetch product data
    try {
      const response = await fetch(
        `/api/products/${item.productId}?forCustomerCatalog=1`
      );
      if (response.ok) {
        const product = await response.json();
        optionGroups = product.optionGroups || [];
        additionals = (product.additionals || []).filter((additional: ProductAdditional) => additional.active !== false);
        basePrice = product.price || item.price;
      }
    } catch (error) {
      console.error('Failed to fetch product data for price calculation:', error);
      // Fallback to stored price if fetch fails
      return item.price;
    }
  }

  let total = Number(basePrice) || 0;

  // Add option price modifiers
  if (item.foodSelections.optionGroups) {
    item.foodSelections.optionGroups.forEach((groupSelection) => {
      const group = optionGroups.find(g => g.id === groupSelection.groupId);
      if (group) {
        groupSelection.optionIds.forEach(optionId => {
          const option = group.options.find(o => o.id === optionId);
          if (option) {
            const priceModifier = Number(option.priceModifier) || 0;
            total += priceModifier;
          }
        });
      }
    });
  }

  // Add additional prices
  if (item.foodSelections.additionals) {
    item.foodSelections.additionals.forEach((additionalSelection) => {
      const additional = additionals.find(a => a.id === additionalSelection.additionalId);
      if (additional && additionalSelection.quantity > 0) {
        const additionalPrice = additionalEffectivePrice(additional);
        const quantity = Number(additionalSelection.quantity) || 0;
        total += additionalPrice * quantity;
      }
    });
  }

  return total;
}

/**
 * Calculate cart item price synchronously if product data is already available
 */
export function calculateCartItemPriceSync(
  item: CartItem,
  productData: CartProductPricing,
): number {
  // If no food selections, return the stored price
  if (!item.foodSelections) {
    return Number(item.price) || 0;
  }

  const optionGroups = productData.optionGroups || [];
  const additionals = (productData.additionals || []).filter((additional) => additional.active !== false);
  const basePrice = Number(productData.basePrice ?? item.price) || 0;

  let total = basePrice;

  // Add option price modifiers
  if (item.foodSelections.optionGroups) {
    item.foodSelections.optionGroups.forEach((groupSelection) => {
      const group = optionGroups.find(g => g.id === groupSelection.groupId);
      if (group) {
        groupSelection.optionIds.forEach(optionId => {
          const option = group.options.find(o => o.id === optionId);
          if (option) {
            const priceModifier = Number(option.priceModifier) || 0;
            total += priceModifier;
          }
        });
      }
    });
  }

  // Add additional prices
  if (item.foodSelections.additionals) {
    item.foodSelections.additionals.forEach((additionalSelection) => {
      const additional = additionals.find(a => a.id === additionalSelection.additionalId);
      if (additional && additionalSelection.quantity > 0) {
        const additionalPrice = additionalEffectivePrice(additional);
        const quantity = Number(additionalSelection.quantity) || 0;
        total += additionalPrice * quantity;
      }
    });
  }

  return total;
}
